import { beginSend, endSend } from 'actions/composition/lib/in_flight_sends';
import { DomainError } from 'scripts/application/lib/error';
import ErrorReporter from 'scripts/infrastructure/error_reporter';
import getCompositionsStore from 'actions/customer/lib/get_compositions_store';
import { handleCommonErrors } from 'scripts/application/lib/gateway_error_interactive_handler';
import removeComposition from 'actions/composition/lib/remove_composition';
import SendEmail, {
  asEmailAttachment,
  getDeletedInlineImages,
  getInlineAttachments,
  getRecipientsList,
  validateTotalSize,
  validateUploads,
} from 'actions/composition/send_email';
import SubmitComposition from 'actions/composition/lib/submit_composition';

// Nothing is added to history optimistically, so the draft is only cleared once the send lands. By then
// the agent may be on another customer, so everything after the request uses the draft's own customer.
export default class SendThreadEmail extends SubmitComposition {
  run({ compositionId, content }) {
    const composition = this.loadComposition(compositionId);
    if (!composition || !beginSend(this.context, composition.id)) {
      return;
    }

    // A throw before the request is out must release the claim, or the draft could never be sent again.
    try {
      return this.send(composition, content);
    } catch (err) {
      endSend(this.context, composition.id);
      throw err;
    }
  }

  send(composition, content) {
    const composed = { ...composition.toJs().content, ...content };
    const recipients = threadRecipients(composed);

    const errors = this.validate(composition, composed, recipients);
    if (errors.length) {
      this.compositionsFor(composition).setErrors(composition.id, errors);
      this.saveComposedContent(composition, content);
      endSend(this.context, composition.id);
      return;
    }

    // A failure clearing the draft is not a failed send: reporting it as one would invite a duplicate.
    return this.postEmail(composed, threadEmailPayload(composition, composed, recipients))
      .then(
        () => this.clearSentComposition(composition),
        err => this.onSendError(composition, content, err)
      )
      .finally(() => endSend(this.context, composition.id));
  }

  validate(composition, composed, recipients) {
    const emailContent = {
      ...recipients,
      bodyPlain: composed.bodyPlain || '',
      from: composed.from,
      subject: composed.subject || composed.subjectHtml,
    };

    return SendEmail.validateContent(emailContent)
      .concat(validateUploads(composition.attachments))
      .concat(
        validateTotalSize(JSON.stringify({ ...emailContent, bodyHtml: composed.bodyHtml }), composition.attachments)
      );
  }

  clearSentComposition(composition) {
    try {
      this.clearComposition(composition);
    } catch (err) {
      ErrorReporter.reportError(err, { message: 'could not clear a sent thread email draft' });
    }
  }

  // The backend copies the uploads out of the draft's upload path after the request lands, so they are
  // detached first: removing the draft would otherwise delete them.
  clearComposition(composition) {
    if (!this.compositionsFor(composition)) {
      this.clearUnloadedComposition(composition);
      return;
    }

    const draft = this.storedDraft(composition);
    if (!draft) {
      return;
    }

    draft.detachUploads();
    this.compositionsFor(composition).replace(draft);
    this.closeSentComposition(draft);
  }

  // A customer is only unloaded after the agent has left it, and leaving flushes the draft to the server.
  clearUnloadedComposition(composition) {
    composition.detachUploads();
    composition.markAsPersisted();
    this.closeSentComposition(composition);
  }

  closeSentComposition(composition) {
    removeComposition(this.context, composition);
  }

  // Keeps the draft: nothing was added to history, so a cleared composition would lose the email.
  onSendError(composition, content, err) {
    const draft = this.storedDraft(composition);
    if (draft) {
      this.saveComposedContent(draft, content);

      if (err instanceof DomainError) {
        this.compositionsFor(composition).setErrors(composition.id, err.errors);
        return;
      }
    }

    handleCommonErrors(this.context, err);
  }

  // The send holds the copy it loaded, but leaving the customer while the request is out flushes the
  // stored draft and marks it persisted. Writing the stale copy back would undo that, and the draft
  // would then never be deleted from the server.
  storedDraft(composition) {
    const compositions = this.compositionsFor(composition);
    if (!compositions || !compositions.has({ id: composition.id })) {
      return undefined;
    }
    return compositions.find(composition.id);
  }

  compositionsFor(composition) {
    return getCompositionsStore(this.context, composition.customerId);
  }
}

function threadRecipients(composed) {
  return {
    to: getRecipientsList(composed.to),
    cc: getRecipientsList(composed.cc),
    bcc: getRecipientsList(composed.bcc),
  };
}

// Inline images the agent deleted from the body are dropped before the send, as SendEmail does.
function threadEmailPayload(composition, composed, recipients) {
  const bodyHtml = composed.bodyHtml || '';
  getDeletedInlineImages(bodyHtml, composition.attachments).forEach(a => composition.removeAttachment(a.id));

  const uploaded = composition.attachments.map(attachment => asEmailAttachment(composition, attachment));
  const inline = getInlineAttachments(bodyHtml, composed.inlineAttachments);

  return {
    from: composed.from,
    to: asAddresses(recipients.to),
    cc: asAddresses(recipients.cc),
    bcc: asAddresses(recipients.bcc),
    subject: composed.subject || composed.subjectHtml,
    body: { html: composed.bodyHtml, text: composed.bodyPlain },
    attachments: inline.concat(uploaded),
  };
}

function asAddresses(emails) {
  return emails.map(email => ({ email }));
}
